import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { executor } from '@nsr/react-query-builder-executor';
import { ContentActionOptions, ConditionsSchema } from '../Admin/ConditionalLogicDialog';
import { CONSTANTS } from '../Helpers/Configuration';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import { setErrors } from '@nsr/react-form';
export const isConditionallyRequiredContext = React.createContext(false);

const selectAllValues = state => state.form.values;
const selectFormErrors = state => state.form.errors;
const selectHiddenPageIds = state => state.settings?.hiddenPageIds;
const { appTemplateControlTypes } = CONSTANTS;

const ConditionalLogicCheck = props => {
    const {
        children = null,
        conditions = [],
        content = null,
        contentType = appTemplateControlTypes.page,
        pageId = null,
        prefix = null,
        repeatingSection = false,
        sectionId = null,
    } = props;
    
    const dispatch = useDispatch();
    const formErrors = useSelector(selectFormErrors);
    const hiddenPageIds = useSelector(selectHiddenPageIds);
    const selectValuesOnlyIfConditions = useMemo(() => {
        if (conditions.length > 0) {
            return selectAllValues;
        }
        return () => true;
    }, [conditions]);
    const allValues = useSelector(selectValuesOnlyIfConditions);
    const values = useMemo(()=>{
        const allValuesCopy = cloneDeep(allValues);
        const pageIds = Object.keys(allValuesCopy);

        pageIds.forEach(pageId => {

            if (hiddenPageIds && hiddenPageIds.includes(pageId)) {
                const page = allValuesCopy[pageId];
                
                if(page) {
                    Object.keys(page).forEach(question => {
                        page[question] = null;
                    });
                }
            }  
        });
        return allValuesCopy;
    },[allValues, hiddenPageIds]);
    
    const [metConditions, setMetConditions] = useState(false);
    const [isConditionallyRequired, setIsConditionallyRequired] = useState(false);

    useEffect(() => {
        const { effect } = ConditionsSchema;

        const updateConditionForRepeatedSection = (query, repeatingSectionIndex) => {
            query.where.forEach(it => {
                const { value = {} } = it?.attribute || null;
                if (value?.sectionId === sectionId) {
                    value.repeatingSection = true;
                    value.repeatingSectionIndex = repeatingSectionIndex;
                }
            });
        };

        const unsetFormError = () => {
            if (!content) {
                return [];
            }
            let errors = {}, errorPath;
            switch (contentType) {
                case appTemplateControlTypes.question:
                    errorPath = prefix ? `${prefix}.${content.id}` : content.id;
                    errors[errorPath] = null;
                    break;
                case appTemplateControlTypes.section:
                    if (!content.showRepetition) {
                        content.groups?.forEach(g => {
                            g.questions?.forEach(q => {
                                if (get(formErrors, `${pageId}.${q.id}`, null)) {
                                    errors[`${pageId}.${q.id}`] = null;
                                }
                            });
                        });
                    }
                    else {
                        if (get(formErrors, `${pageId}.${content.id}`, null)) {
                            errors[`${pageId}.${content.id}`] = null;
                        }
                    }
                    break;
                case appTemplateControlTypes.page:
                    content.sections?.forEach(s => {
                        if (!s.showRepetition) {
                            s.groups?.forEach(g => {
                                g.questions?.forEach(q => {
                                    if (get(formErrors, `${content.id}.${q.id}`, null)) {
                                        errors[`${content.id}.${q.id}`] = null;
                                    }
                                });
                            });
                        }
                        else {
                            if (get(formErrors, `${content.id}.${s.id}`, null)) {
                                errors[`${content.id}.${s.id}`] = null;
                            }
                        }
                    });
                    break;
                default:
                    break;
            }
            return errors;
        };

        setIsConditionallyRequired(false);
        const fn = async () => {
            if (conditions.length > 0) {
                const conditionsClone = cloneDeep(conditions);
                for (let i = 0; i < conditionsClone.length; i++) {
                    const condition = conditionsClone[i];
                    const { fn = null, query } = condition;
                    if (contentType === appTemplateControlTypes.question && repeatingSection) {
                        const prefixArr = prefix?.split('.');
                        const repeatingSectionIndex = prefixArr ? prefixArr[prefixArr.length - 1] : '0';
                        updateConditionForRepeatedSection(query, repeatingSectionIndex);
                    }
                    const evaluateCondition = fn || executor(query);
                    const result = await evaluateCondition(values);
                    condition.result = result;
                }
                // If any condition evaluates to hide a field, set both states to false
                if (conditionsClone.some(c => (c[effect] === ContentActionOptions.hide && c.result) ||
                    (c[effect] === ContentActionOptions.show && !c.result))) {
                    setMetConditions(false);
                    setIsConditionallyRequired(false);
                    const errors = unsetFormError();
                    if (Object.keys(errors).length > 0) {
                        dispatch(setErrors(errors));
                    }
                }
                else {
                    setMetConditions(true);
                    // Required condition
                    if (conditionsClone.some(c => c[effect] === ContentActionOptions.required && c.result)) {
                        setIsConditionallyRequired(true);
                    } else {
                        setIsConditionallyRequired(false);
                    }
                }
            }
            else {
                setMetConditions(true);
            }
        };
        fn();
    }, [conditions, values]);
    if (!metConditions) {
        return null;
    }
    return (
        <isConditionallyRequiredContext.Provider value={isConditionallyRequired}>
            {children}
        </isConditionallyRequiredContext.Provider>
    );
};
ConditionalLogicCheck.propTypes = {
    children: PropTypes.node,
    conditions: PropTypes.array,
    content: PropTypes.object,
    contentType: PropTypes.string,
    pageId: PropTypes.string,
    prefix: PropTypes.string,
    repeatingSection: PropTypes.bool,
    sectionId: PropTypes.string,
};
export default ConditionalLogicCheck;
